// The Products step's issues summary, pure (bos-app#213, US-02): the kinds
// of problem the last check found, each with how many rows, variants, SKUs
// or barcodes it covers and the bulk fix that answers it (or none), and the
// kind the table is filtered to (`?issue=`).
import {
    CATALOG_IMPORT_CODE_KINDS,
    type CatalogImportCode,
} from '@/api/handlers/resources/catalogImports';
import { CELL_NOT_A_NUMBER } from '@/pages/catalogImport/notices';
import {
    bulkFixGroups,
    groupsWithFix,
    isBulkFixCode,
    type BulkFixCode,
    type BulkFixSource,
} from '@/pages/catalogImport/store';

/** The kinds with a bulk fix, each answering one problem code with its default. */
export type FixableIssueKind =
    'imei' | 'variantFields' | 'groupFields' | 'sharedBarcode';

/**
 * The kinds of blocking problem with no default fix (#213, "No default"):
 * field checks (a required cell empty, a value too long, not a number, not
 * whole, negative, or not a VAT type or unit the template allows), unknown
 * or ambiguous references (a brand, category or supplier, a Names choice, a
 * variant or an attribute), a barcode that matches several variants or
 * another product than its SKU, and attribute problems. A problem code this
 * app does not know is `other`. Each is counted in rows, and the owner
 * corrects or excludes each row.
 */
export type NoDefaultIssueKind =
    'fieldChecks' | 'references' | 'barcodeMatch' | 'attributes' | 'other';

/**
 * A kind of problem the summary lists: the four with a default fix, the
 * blocking ones with none, and the extra barcodes the import keeps on its
 * own ("Done for you").
 */
export type IssueKind = FixableIssueKind | NoDefaultIssueKind | 'alternates';

/** The kinds with a bulk fix, in the order the summary lists them. */
export const FIXABLE_ISSUE_KINDS: readonly FixableIssueKind[] = [
    'imei',
    'variantFields',
    'groupFields',
    'sharedBarcode',
];

/** The kinds with no default fix, in the order the summary lists them. */
export const NO_DEFAULT_ISSUE_KINDS: readonly NoDefaultIssueKind[] = [
    'fieldChecks',
    'references',
    'barcodeMatch',
    'attributes',
    'other',
];

/**
 * The kinds in the order the summary lists them: those with a fix, those
 * that block with none, then the extra barcodes, which block nothing.
 */
export const ISSUE_KINDS: readonly IssueKind[] = [
    ...FIXABLE_ISSUE_KINDS,
    ...NO_DEFAULT_ISSUE_KINDS,
    'alternates',
];

export const isIssueKind = (value: unknown): value is IssueKind =>
    ISSUE_KINDS.includes(value as IssueKind);

export const isFixableIssueKind = (kind: IssueKind): kind is FixableIssueKind =>
    FIXABLE_ISSUE_KINDS.includes(kind as FixableIssueKind);

/**
 * The bulk fix of each kind; `null` for a kind with no default, and for the
 * extra barcodes, which the import keeps on its own.
 */
export const ISSUE_KIND_FIX = {
    imei: 'BARCODE_LOOKS_LIKE_IMEI',
    variantFields: 'VARIANT_FIELDS_DIFFER',
    groupFields: 'GROUP_FIELDS_DIFFER',
    sharedBarcode: 'BARCODE_SHARED_ACROSS_SKUS',
    fieldChecks: null,
    references: null,
    barcodeMatch: null,
    attributes: null,
    other: null,
    alternates: null,
} as const satisfies Record<IssueKind, BulkFixCode | null>;

/** The row problems bos-api answers with (kind `problem`). */
type ProblemCode = {
    [
        Code in CatalogImportCode
    ]: (typeof CATALOG_IMPORT_CODE_KINDS)[Code] extends 'problem'
        ? Code
        : never;
}[CatalogImportCode];

/**
 * The kind of each row problem with no default fix, the client's unreadable
 * number cell (`CELL_NOT_A_NUMBER`) among the field checks. Typed over every
 * problem code, so a code bos-api adds needs a kind here.
 */
const NO_DEFAULT_KIND = {
    SKU_REQUIRED: 'fieldChecks',
    NAME_REQUIRED: 'fieldChecks',
    SALE_PRICE_REQUIRED: 'fieldChecks',
    VALUE_TOO_LONG: 'fieldChecks',
    QUANTITY_REQUIRED: 'fieldChecks',
    QUANTITY_NOT_WHOLE: 'fieldChecks',
    COST_REQUIRED: 'fieldChecks',
    AMOUNT_NEGATIVE: 'fieldChecks',
    VAT_TYPE_UNKNOWN: 'fieldChecks',
    UNIT_UNKNOWN: 'fieldChecks',
    [CELL_NOT_A_NUMBER]: 'fieldChecks',
    BRAND_AMBIGUOUS: 'references',
    CATEGORY_AMBIGUOUS: 'references',
    SUPPLIER_AMBIGUOUS: 'references',
    NAME_TARGET_UNKNOWN: 'references',
    NAME_MERGE_CYCLE: 'references',
    VARIANT_UNKNOWN: 'references',
    ATTRIBUTE_TYPE_UNKNOWN: 'references',
    ATTRIBUTE_VALUE_UNKNOWN: 'references',
    BARCODE_AMBIGUOUS: 'barcodeMatch',
    SKU_BARCODE_CONFLICT: 'barcodeMatch',
    AXES_DIFFER_FROM_PRODUCT: 'attributes',
    PRODUCT_IS_SIMPLE: 'attributes',
    GROUP_AXES_DIFFER: 'attributes',
    AXIS_NOT_ON_CATEGORY: 'attributes',
    CATEGORY_REQUIRED_FOR_OPTIONS: 'attributes',
} as const satisfies Record<
    Exclude<ProblemCode, BulkFixCode> | typeof CELL_NOT_A_NUMBER,
    Exclude<NoDefaultIssueKind, 'other'>
>;

/** The kind of a row problem with no default fix; `null` for one with a fix. */
const noDefaultKindOf = (code: string): NoDefaultIssueKind | null => {
    if (isBulkFixCode(code)) {
        return null;
    }
    return Object.hasOwn(NO_DEFAULT_KIND, code)
        ? NO_DEFAULT_KIND[code as keyof typeof NO_DEFAULT_KIND]
        : 'other';
};

const BARCODES_KEPT_AS_ALTERNATES =
    'BARCODES_KEPT_AS_ALTERNATES' satisfies CatalogImportCode;

/** One kind of problem the last check found. */
export type IssueEntry = {
    kind: IssueKind;
    /**
     * Its bulk fix; `null` for a kind with no default, for a fix kind whose
     * default changes none of its rows, and for the extra barcodes, which
     * the import keeps.
     */
    fix: BulkFixCode | null;
    /**
     * How many it covers, in the kind's own unit: rows (IMEIs, every kind
     * with no default, and a fix kind with no fix left), the variants, SKUs
     * or barcodes the fix changes, or variants with extra barcodes. The bulk
     * fix's N.
     */
    count: number;
    /** The rows it is on, in row order: what Show rows lists. */
    rows: number[];
};

const sortedRows = (rows: Iterable<number>): number[] =>
    [...new Set(rows)].sort((a, b) => a - b);

const numberRows = (value: unknown): number[] =>
    Array.isArray(value)
        ? value.filter((row): row is number => Number.isInteger(row))
        : [];

/**
 * The variants whose extra barcodes the import keeps: the rows that carry
 * `BARCODES_KEPT_AS_ALTERNATES`, one variant per set of `params.rows`, as
 * new-variant lines have no group key.
 */
const alternatesEntry = (source: BulkFixSource): IssueEntry => {
    const variants = new Set<string>();
    const rows: number[] = [];
    for (const line of source.preview?.lines ?? []) {
        if (source.exclusions.has(line.rowNumber)) {
            continue;
        }
        const warning = line.warnings.find(
            (candidate) => candidate.code === BARCODES_KEPT_AS_ALTERNATES,
        );
        if (!warning) {
            continue;
        }
        rows.push(line.rowNumber);
        variants.add(
            sortedRows([
                ...numberRows(warning.params?.rows),
                line.rowNumber,
            ]).join(','),
        );
    }
    return {
        kind: 'alternates',
        fix: null,
        count: variants.size,
        rows: sortedRows(rows),
    };
};

/**
 * The blocking problems with no default fix, by kind: the rows the user has
 * not excluded with at least one problem of the kind, each counted once.
 */
const noDefaultEntries = (
    source: BulkFixSource,
): Map<NoDefaultIssueKind, IssueEntry> => {
    const rowsByKind = new Map<NoDefaultIssueKind, Set<number>>();
    for (const line of source.preview?.lines ?? []) {
        if (
            line.status === 'excluded' ||
            source.exclusions.has(line.rowNumber)
        ) {
            continue;
        }
        for (const problem of line.problems) {
            const kind = noDefaultKindOf(problem.code);
            if (kind === null) {
                continue;
            }
            const rows = rowsByKind.get(kind) ?? new Set<number>();
            rows.add(line.rowNumber);
            rowsByKind.set(kind, rows);
        }
    }
    const entries = new Map<NoDefaultIssueKind, IssueEntry>();
    rowsByKind.forEach((rows, kind) => {
        entries.set(kind, {
            kind,
            fix: null,
            count: rows.size,
            rows: sortedRows(rows),
        });
    });
    return entries;
};

/**
 * A fix kind: the groups its default changes (`groupsWithFix`), and Show
 * rows over every group of it. When the default changes none of them (a
 * variant that differs only by an unreadable cell), the kind has no fix and
 * is counted in rows, as the kinds with no default are.
 */
const fixEntry = (
    source: BulkFixSource,
    kind: FixableIssueKind,
    fix: BulkFixCode,
): IssueEntry => {
    const groups = bulkFixGroups(source, fix).filter(
        (group) => group.rows.length > 0,
    );
    const rows = sortedRows(groups.flatMap((group) => group.rows));
    const fixed = groupsWithFix(groups);
    return fixed.length > 0
        ? { kind, fix, count: fixed.length, rows }
        : { kind, fix: null, count: rows.length, rows };
};

/**
 * The kinds the last check found on rows the user has not excluded, in
 * `ISSUE_KINDS` order, each once; a kind with nothing to count is left out.
 * The fix kinds count the groups `catalogImportBulkFixes` finds (rows for
 * the IMEI move, variants, SKUs, shared barcodes); the kinds with no default
 * count rows, a row with problems of several kinds in each of them.
 */
export const issueEntriesOf = (source: BulkFixSource): IssueEntry[] => {
    const noDefaults = noDefaultEntries(source);
    return ISSUE_KINDS.map((kind): IssueEntry | undefined => {
        if (kind === 'alternates') {
            return alternatesEntry(source);
        }
        if (isFixableIssueKind(kind)) {
            return fixEntry(source, kind, ISSUE_KIND_FIX[kind]);
        }
        return noDefaults.get(kind);
    }).filter(
        (entry): entry is IssueEntry => entry !== undefined && entry.count > 0,
    );
};

// ---- The address ------------------------------------------------------------

/** The address parameter that filters the table to one kind: `?issue=imei`. */
export const ISSUE_FILTER_PARAM = 'issue';

/** The kind the address filters to; `null` for none, or one this app does not know. */
export const readIssueFilter = (params: URLSearchParams): IssueKind | null => {
    const value = params.get(ISSUE_FILTER_PARAM);
    return isIssueKind(value) ? value : null;
};

/** The address filtered to `kind` (left out for `null`), every other parameter kept. */
export const writeIssueFilter = (
    params: URLSearchParams,
    kind: IssueKind | null,
): URLSearchParams => {
    const next = new URLSearchParams(params);
    if (kind === null) {
        next.delete(ISSUE_FILTER_PARAM);
    } else {
        next.set(ISSUE_FILTER_PARAM, kind);
    }
    return next;
};
